import React, { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Loader } from "@vibe/core";
import { AddItemDropdown } from "@components/addItem";
import AddItemProgressBanner from "@components/ItemViewSection/AddItemProgressBanner";
import useItemLocations from "@hooks/api/useItemLocations";
import useUser from "@hooks/context/userHook";
import { useErrorWebsocket } from "@hooks/services";
import { useBigAccount } from "@hooks/ui";
import useAddItemInProgress from "@hooks/ui/useAddItemInProgress";
import useSimulatedProgress from "@hooks/ui/useSimulatedProgress";
import useRevalidateOnAddItemCompleted from "@hooks/ui/useRevalidateOnAddItemCompleted";
import { setItemConnected } from "@redux/slices/itemConnectedSlice";

/**
 * Body of the "Shared Boards 🔒" column dialog: the same workspace → board → group picker the
 * item view opens from "Add to board", shown the moment the cell is clicked. The cell's own
 * chips already list the boards the item is on, so the dialog keeps no board list of its own.
 * Nothing here writes the column value — the services maintain the chips as sync state changes.
 */
const BoardsDialogContent = () => {
  const { userId, accountId } = useUser();
  const { error, tableData, itemIds, mutateItemLocations } = useItemLocations();
  const bigAccount = useBigAccount();
  const dispatch = useDispatch();

  // The socket `success` handler refreshes item locations and broadcasts
  // `add-item-completed`; the BroadcastChannel hook catches a completion seen
  // by another SIMB iframe (e.g. the item view open in the same tab).
  useErrorWebsocket(
    userId ? String(userId) : "",
    accountId ? String(accountId) : "",
    itemIds?.[0]?.toString() || "",
  );
  const revalidate = useCallback(() => {
    mutateItemLocations();
  }, [mutateItemLocations]);
  useRevalidateOnAddItemCompleted(revalidate);

  // Same signal the item view sets, kept in step for the surfaces that read it.
  useEffect(() => {
    if (tableData) {
      dispatch(setItemConnected(!tableData.noLinkedItems));
    }
  }, [tableData, dispatch]);

  const { isAddingItem, hasFailed: addItemFailed } = useAddItemInProgress();
  const {
    progress,
    isVisible: showProgress,
    isCompleted,
    hasFailed,
  } = useSimulatedProgress(isAddingItem, addItemFailed);

  // The picker waits for the item-locations request so the connected flag is right
  // before the first group click; if that request fails the picker still opens.
  const ready = !!tableData || !!error;

  return (
    <div className="flex flex-col w-full h-screen overflow-hidden bg-white">
      {showProgress && (
        <AddItemProgressBanner
          compact
          progress={progress}
          isCompleted={isCompleted}
          hasFailed={hasFailed}
        />
      )}

      {ready ? (
        <div className="flex-1 min-h-0">
          <AddItemDropdown
            centered={false}
            fullWidth
            bigAccount={bigAccount}
            stepsEnabled={false}
            fullWidthHeight="100%"
          />
        </div>
      ) : (
        <div className="flex flex-1 justify-center items-center">
          <Loader size="small" color="primary" />
        </div>
      )}
    </div>
  );
};

export default BoardsDialogContent;
